import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useIonActionSheet, useIonAlert } from '@ionic/react';
import { CalendarClock, Car, XCircle } from 'lucide-react';
import { Button, Card, Notice } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime, fromLocalInput, toLocalInput } from '../../lib/format';
import { formatMoney } from '@tcrd/shared';
import type { RideDto } from '../../lib/types';
import type { VehicleDto } from '../driver/vehicles/types';

type RideWithBooking = RideDto & {
  reschedule: { proposedAt: string; byRole: string; note: string | null; mine: boolean } | null;
  lateCancellation?: boolean;
};

/** Acciones sobre una reserva confirmada: reprogramar, cancelar, cambiar vehículo. */
export function BookingActions({ ride, role }: { ride: RideWithBooking; role: 'customer' | 'driver' }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [alert] = useIonAlert();
  const [sheet] = useIonActionSheet();
  const [editing, setEditing] = useState(false);
  const [when, setWhen] = useState(toLocalInput(new Date(ride.scheduledAt)));
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: () => api<VehicleDto[]>('/vehicles'), enabled: role === 'driver' });

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['ride', ride.id] }), qc.invalidateQueries({ queryKey: ['rides'] })]);
  const run = (fn: () => Promise<unknown>, ok: string) =>
    fn()
      .then(refresh)
      .then(() => toast(ok, 'success'))
      .catch((e) => toast(errorMessage(e), 'danger'));

  const editable = ride.status === 'CONFIRMED';
  const cancellable = ['CONFIRMED', 'DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED'].includes(ride.status);
  const lateSoon = new Date(ride.scheduledAt).getTime() - Date.now() < 2 * 3600_000;

  return (
    <div className="flex flex-col gap-3">
      {ride.reschedule && (
        <Card className="p-4 border-2 border-amber-400/70">
          <p className="text-sm">
            {ride.reschedule.mine ? 'Propusiste' : role === 'customer' ? 'El conductor propone' : 'El cliente propone'} mover el viaje al <strong>{fmtDateTime(ride.reschedule.proposedAt)}</strong>.
            {ride.reschedule.note ? <span data-no-translate>{` “${ride.reschedule.note}”`}</span> : null}
          </p>
          {!ride.reschedule.mine && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button size="sm" variant="secondary" onClick={() => run(() => api(`/bookings/${ride.id}/reschedule/respond`, { body: { accept: false } }), 'Propuesta rechazada')}>Rechazar</Button>
              <Button size="sm" variant="accent" onClick={() => run(() => api(`/bookings/${ride.id}/reschedule/respond`, { body: { accept: true } }), 'Nuevo horario confirmado')}>Aceptar</Button>
            </div>
          )}
        </Card>
      )}

      {editable && !ride.reschedule && (
        editing ? (
          <Card className="p-4 flex flex-col gap-3">
            <label className="text-sm font-medium" htmlFor="new-when">Nueva fecha y hora</label>
            <input id="new-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-12 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4" />
            <p className="text-xs text-[var(--text-muted)]">El precio acordado se mantiene. La otra parte debe aceptar el cambio.</p>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
              <Button size="sm" onClick={() => run(() => api(`/bookings/${ride.id}/reschedule`, { body: { scheduledAt: fromLocalInput(when) } }), 'Propuesta enviada').then(() => setEditing(false))}>Proponer</Button>
            </div>
          </Card>
        ) : (
          <Button variant="secondary" block icon={<CalendarClock className="size-5" />} onClick={() => setEditing(true)}>Reprogramar</Button>
        )
      )}

      {role === 'driver' && ['CONFIRMED', 'DRIVER_ON_THE_WAY'].includes(ride.status) && (
        <Button
          variant="secondary"
          block
          icon={<Car className="size-5" />}
          onClick={() =>
            sheet({
              header: 'Asignar vehículo',
              buttons: [
                ...(vehicles.data ?? [])
                  .filter((v) => v.status === 'ACTIVE')
                  .map((v) => ({ text: `${v.make} ${v.model} · ${v.plate}${v.id === ride.vehicle?.id ? ' (actual)' : ''}`, handler: () => void run(() => api(`/bookings/${ride.id}/vehicle`, { method: 'PUT', body: { vehicleId: v.id } }), 'Vehículo actualizado; se avisó al cliente') })),
                { text: 'Cancelar', role: 'cancel' },
              ],
            })
          }
        >
          Cambiar vehículo
        </Button>
      )}

      {cancellable && (
        <>
          {role === 'customer' && lateSoon && <Notice tone="warning">Faltan menos de 2 horas: revisa la política de cancelación del negocio.</Notice>}
          <Button
            variant="danger"
            block
            icon={<XCircle className="size-5" />}
            onClick={async () => {
              // El cliente ve el cargo por cancelación (si aplica) antes de confirmar.
              const preview = role === 'customer' ? await api<{ fee: string | null }>(`/bookings/${ride.id}/cancel-preview`).catch(() => ({ fee: null })) : { fee: null };
              alert({
                header: '¿Cancelar este viaje?',
                message: preview.fee ? `Tu conductor ya va en camino: se aplicará un cargo por cancelación de ${formatMoney(preview.fee, ride.currency)}.` : undefined,
                inputs: [{ name: 'reason', type: 'text', placeholder: 'Motivo (opcional)' }],
                buttons: [
                  { text: 'Volver', role: 'cancel' },
                  { text: 'Cancelar viaje', role: 'destructive', handler: (v: { reason: string }) => void run(() => api(`/bookings/${ride.id}/cancel`, { body: { reason: v.reason || undefined } }), 'Viaje cancelado') },
                ],
              });
            }}
          >
            Cancelar viaje
          </Button>
        </>
      )}
    </div>
  );
}
